import { Box } from '@mui/material';
import { useForm } from '../FormContext';

/**
 * This is the Higher Order Component Pattern
 * This returns a new component that will conditionally render based on a condition which accepts formValues as the argument.
 *
 * Options:
 *   keepMounted (default false) — when true, the wrapped component stays mounted and is hidden via
 *     CSS (display:none) instead of being removed from the tree. Use this when the field's form
 *     value should be preserved across visibility toggles (e.g. dashboard filter fields).
 *
 * ex: const MyConditionalFormField = withConditionalRender((formValues) => true)(DynamicFormField);
 * ex: const MyConditionalFormField = withConditionalRender((formValues) => true, { keepMounted: true })(DynamicFormField);
 */
export const withConditionalRender = (condition, { keepMounted = false } = {}) => (WrappedComponent) => {
  return (props) => {
    const { formValues } = useForm();

    if (keepMounted) {
      return (
        <Box sx={{ display: condition(formValues) ? 'contents' : 'none' }}>
          <WrappedComponent {...props} />
        </Box>
      );
    }

    if (condition(formValues)) {
      return <WrappedComponent {...props} />;
    }

    return null;
  };
};
